import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, invitesApi, CREDITS_EVENT } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: { id, email, role, business: { id, name, kind, credits, unlimited_credits, webhook_key? } }
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const data = await authApi.me();
      setUser(data.user);
      return data.user;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Check auth status on mount
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // AI calls report the new balance; keep it current without refetching
  useEffect(() => {
    const onCredits = (event) => {
      setUser(current => current && {
        ...current,
        business: { ...current.business, credits: event.detail ?? current.business.credits },
      });
    };
    window.addEventListener(CREDITS_EVENT, onCredits);
    return () => window.removeEventListener(CREDITS_EVENT, onCredits);
  }, []);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    setUser(data.user);
    return data.user;
  };

  const register = async (details) => {
    const data = await authApi.register(details);
    setUser(data.user);
    return data.user;
  };

  const acceptInvite = async (token, email, password) => {
    const data = await invitesApi.accept(token, email, password);
    setUser(data.user);
    return data.user;
  };

  const acceptInviteWithGoogle = async (token, credential) => {
    const data = await invitesApi.acceptWithGoogle(token, credential);
    setUser(data.user);
    return data.user;
  };

  const googleSignIn = async (credential, details) => {
    const data = await authApi.google(credential, details);
    setUser(data.user);
    return data.user;
  };

  const resetPassword = async (token, password) => {
    const data = await authApi.resetPassword(token, password);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    acceptInvite,
    acceptInviteWithGoogle,
    resetPassword,
    googleSignIn,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
